import type { Config } from 'effect';
import type { ImagePathAbstractOption, ImagePathOption } from '../type/imagePath';

import { Effect, Option } from 'effect';

import { resolveImageUrlEffect } from '@vs-shop/engine/image/resolveImageUrl';

import { BannerVariant, type BannerVariant as BannerVariantT } from '@vs-shop/commerce-ui/banner';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

type BannerImagePathAbstractOption = {
  desktop: ImagePathAbstractOption;
  tablet: ImagePathAbstractOption;
  mobile: ImagePathAbstractOption;
};

export type BannerImagePathOptions = {
  desktop: ImagePathOption;
  tablet: ImagePathOption;
  mobile: ImagePathOption;
};

type BannerFilters = {
  desktop: typeof ImageFilter.banner_desktop | typeof ImageFilter.banner_listing;
  tablet: typeof ImageFilter.banner_tablet | typeof ImageFilter.banner_listing;
  mobile: typeof ImageFilter.banner_mobil | typeof ImageFilter.banner_listing;
};

function resolveFirstImagePath(
  candidates: readonly ImagePathAbstractOption[],
  filter: BannerFilters[keyof BannerFilters]
): Effect.Effect<ImagePathAbstractOption, Config.ConfigError> {
  const firstSomeOf = Option.firstSomeOf(candidates);

  return Option.match(firstSomeOf, {
    onNone: () => Effect.succeed(Option.none()),
    onSome: (pathAbstract) => resolveImageUrlEffect(pathAbstract, filter).pipe(Effect.asSome),
  });
}

function getFiltersForVariant(variant: BannerVariantT): BannerFilters {
  if (variant === BannerVariant.PRODUCT_CARD) {
    return {
      desktop: ImageFilter.banner_listing,
      tablet: ImageFilter.banner_listing,
      mobile: ImageFilter.banner_listing,
    };
  }

  // Default for BASE and TINY. HERO never reaches here: it is not art-directed,
  // so `getBannerMediaProps` resolves its single crop before calling this.
  return {
    desktop: ImageFilter.banner_desktop,
    tablet: ImageFilter.banner_tablet,
    mobile: ImageFilter.banner_mobil,
  };
}

export const getBannerImagePaths =
  (variant: BannerVariantT) =>
  ({
    desktop,
    tablet,
    mobile,
  }: BannerImagePathAbstractOption): Effect.Effect<BannerImagePathOptions, Config.ConfigError> => {
    const filters = getFiltersForVariant(variant);

    return Effect.all({
      desktop: resolveFirstImagePath([desktop, tablet, mobile], filters.desktop),
      tablet: resolveFirstImagePath([tablet, desktop, mobile], filters.tablet),
      mobile: resolveFirstImagePath([mobile, tablet, desktop], filters.mobile),
    });
  };
